import React, { useContext } from "react";
import { ReactReduxContext } from "react-redux";
import ShowChartRoundedIcon from "@mui/icons-material/ShowChartRounded";
import BarChartRoundedIcon from "@mui/icons-material/BarChartRounded";

// The space a chart takes, so an empty one keeps its card's height.
export const CHART_HEIGHT = 240;

const sameDay = (a, b) => new Date(a).setHours(0, 0, 0, 0) === new Date(b).setHours(0, 0, 0, 0);
const dayName = (date) => new Date(date).toLocaleString("en-IN", { day: "numeric", month: "short" });

// What the empty chart says under its title, for the picked dates: waiting on
// today's readings is not the same as a day that recorded nothing. Without
// dates, a general line.
export const emptyChartHint = (start, end, now = Date.now()) => {
  const valid = (date) => date && !Number.isNaN(new Date(date).getTime());
  if (!valid(start) && !valid(end)) return "Nothing was recorded for the selected dates.";
  const from = valid(start) ? start : end;
  const to = valid(end) ? end : start;
  if (sameDay(from, to)) {
    return sameDay(to, now)
      ? "Nothing yet today. Readings appear here as the device sends them."
      : `Nothing was recorded on ${dayName(to)}. Try another date.`;
  }
  return `Nothing was recorded from ${dayName(from)} to ${dayName(to)}.`;
};

const ICONS = { line: ShowChartRoundedIcon, bar: BarChartRoundedIcon };

// A chart's empty state: a faint grid where the plot would be, with an icon,
// `title` and a hint for the picked dates (read from the store when there is
// one). `kind` picks the icon: "line" or "bar".
export default function ChartEmpty({ title, kind = "line" }) {
  const store = useContext(ReactReduxContext)?.store;
  const state = store?.getState?.() ?? {};
  const hint = emptyChartHint(state.selectedDate, state.selectedEndDay);
  const Icon = ICONS[kind] ?? ShowChartRoundedIcon;

  return (
    <div
      role="status"
      style={{
        position: "relative",
        height: CHART_HEIGHT,
        marginTop: 8,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        fontFamily: "manrope",
        // Four dashed lines, like an empty plot's grid.
        backgroundImage:
          "repeating-linear-gradient(to bottom, transparent 0, transparent 47px, #eef0f3 47px, #eef0f3 48px)",
        backgroundPosition: "0 24px",
        backgroundSize: "100% 192px",
        backgroundRepeat: "no-repeat",
      }}
    >
      <div
        style={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          gap: 6,
          maxWidth: 300,
          padding: "16px 20px",
          borderRadius: 12,
          background: "#ffffff",
          textAlign: "center",
        }}
      >
        <span
          aria-hidden="true"
          style={{
            width: 44,
            height: 44,
            borderRadius: "50%",
            display: "inline-flex",
            alignItems: "center",
            justifyContent: "center",
            background: "#f1f4fb",
            color: "#9aa3b5",
            marginBottom: 4,
          }}
        >
          <Icon style={{ fontSize: 24, fill: "currentColor" }} />
        </span>
        <div style={{ fontSize: 15, fontWeight: 600, color: "#363B3F" }}>{title}</div>
        <div style={{ fontSize: 13, lineHeight: "18px", color: "#8A8F96" }}>{hint}</div>
      </div>
    </div>
  );
}
